import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { api, qs, type SupplierRowData } from '../api';
import { Badge, Checkbox, Empty, ErrorMessage, Field, Input, PageHeader, Select, Spinner, money, number } from '../ui';

const SORTS = [
  ['spent', 'Most spent'],
  ['orders', 'Most orders'],
  ['recent', 'Most recent order'],
  ['name', 'Name'],
] as const;

export function Suppliers() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const sort = SORTS.some(([k]) => k === params.get('sort')) ? params.get('sort')! : 'spent';
  const all = params.get('all') === '1';
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    setParams(next, { replace: true });
  };
  const list = useQuery({
    queryKey: ['suppliers', q, sort, all],
    queryFn: () => api<{ suppliers: SupplierRowData[] }>(`/api/v1/suppliers${qs({ q, sort, includeInactive: all ? 'true' : undefined })}`).then((r) => r.suppliers),
  });

  return (
    <>
      <PageHeader title="Suppliers" subtitle="Everyone you buy from. A supplier is made automatically from the seller on each purchase and order, so there is nothing to set up." />
      <div className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Search" className="w-64">
            <Input placeholder="Name, BrickLink username or tag" value={q} onChange={(e) => set({ q: e.target.value })} />
          </Field>
          <Field label="Sort by">
            <Select value={sort} onChange={(e) => set({ sort: e.target.value })}>
              {SORTS.map(([k, label]) => (
                <option key={k} value={k}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <div className="pb-1.5">
            <Checkbox label="Include retired suppliers" checked={all} onChange={(v) => set({ all: v ? '1' : null })} />
          </div>
        </div>

        {list.isPending ? (
          <Spinner />
        ) : list.error ? (
          <ErrorMessage error={list.error} />
        ) : list.data.length === 0 ? (
          <Empty>{q ? 'No suppliers match.' : 'No suppliers yet. They appear as soon as a purchase or order names a seller.'}</Empty>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="data w-full min-w-[56rem] text-sm">
              <thead>
                <tr>
                  <th>Supplier</th>
                  <th className="text-right">Orders</th>
                  <th className="text-right">Spent</th>
                  <th className="text-right" title="Postage as a share of what the parts cost">Postage</th>
                  <th className="text-right" title="Average days from ordering to receiving">Days to arrive</th>
                  <th className="text-right">Still in stock</th>
                  <th>Last order</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td>
                      <Link to={`/suppliers/${s.id}`} className="font-medium hover:underline">
                        {s.name}
                      </Link>{' '}
                      {!s.active && <Badge>Retired</Badge>}
                      <div className="text-xs text-slate-500">
                        {s.bricklinkUsername && `BrickLink: ${s.bricklinkUsername}`}
                        {s.tags.length > 0 && <span className="ml-2">{s.tags.map((t) => <Badge key={t} tone="blue">{t}</Badge>)}</span>}
                      </div>
                    </td>
                    <td className="text-right tabular-nums">{number(s.orders)}</td>
                    <td className="text-right tabular-nums">{money(s.spent)}</td>
                    <td className="text-right tabular-nums">{s.postagePct === null ? <span className="text-slate-400">—</span> : `${s.postagePct}%`}</td>
                    <td className="text-right tabular-nums">{s.avgDaysToReceive === null ? <span className="text-slate-400">—</span> : s.avgDaysToReceive}</td>
                    <td className="text-right tabular-nums">{s.stockPieces > 0 ? `${number(s.stockPieces)} · ${money(s.stockCost)}` : <span className="text-slate-400">0</span>}</td>
                    <td className="whitespace-nowrap">{s.lastOrder ?? <span className="text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
